import { Link } from "react-router-dom";
import '../../styles/EventsStyles/WebinarCard.css';
import PresenterAvatar from "./PresenterAvatar";
import { Webinar } from "../../webinars/types";
import { availability } from "../../webinars/availability";
import { dateBlock, durationMinutes, formatTimeRange } from "../../webinars/format";

interface WebinarCardProps {
  webinar: Webinar;
  /** Registrations that aren't cancelled */
  activeCount: number;
  /** The presenter's photo, if the webinar has one */
  presenterPhotoUrl?: string;
  /** Position in the grid, used to stagger the scroll reveal */
  revealOrder?: number;
}

export default function WebinarCard({ webinar, activeCount, presenterPhotoUrl, revealOrder = 0 }: WebinarCardProps) {
  const state = availability(webinar, activeCount);
  const date = dateBlock(webinar.startAt);
  const percent = webinar.capacity ? Math.min(100, Math.round((activeCount / webinar.capacity) * 100)) : 0;

  return (
    <article className="km-card ev-card" data-reveal style={{ "--reveal-order": revealOrder } as React.CSSProperties}>
      <div className="ev-card-top">
        <div className="ev-date" aria-hidden="true">
          <span className="ev-date-month">{date.month}</span>
          <span className="ev-date-day">{date.day}</span>
          <span className="ev-date-weekday">{date.weekday}</span>
        </div>
        <div>
          <div className="ev-card-chips">
            <span className={`ev-chip ev-chip--${state.tone}`}>{state.label}</span>
            {webinar.isDemo && <span className="ev-chip ev-chip--demo">Demo</span>}
          </div>
          <h3 className="ev-card-title">{webinar.title}</h3>
        </div>
      </div>

      <p className="ev-card-summary">{webinar.summary || webinar.description}</p>

      <div className="ev-card-presenter">
        <PresenterAvatar name={webinar.presenter || "Kole Medical"} photoUrl={presenterPhotoUrl} size="sm" />
        <span>
          <strong>{webinar.presenter || "Presenter to be announced"}</strong>
          {webinar.presenterRole && <small>{webinar.presenterRole}</small>}
        </span>
      </div>

      <p className="ev-card-meta">
        {formatTimeRange(webinar.startAt, webinar.endAt)} SAST · {durationMinutes(webinar.startAt, webinar.endAt)} min · Online
        {webinar.meetingPlatform ? ` via ${webinar.meetingPlatform}` : ""}
      </p>

      {webinar.capacity ? (
        <div className="ev-seats">
          <div className="ev-seats-track"><div className="ev-seats-fill" style={{ width: `${percent}%` }} /></div>
          <small>{state.isFull ? "Fully booked" : `${state.seatsLeft} of ${webinar.capacity} seats available`}</small>
        </div>
      ) : (
        <p className="ev-seats-note">No attendee limit</p>
      )}

      <Link className="km-btn km-btn--primary km-btn--block ev-card-cta" to={`/events/${webinar.slug}`}>
        {state.canRegister ? "View & register" : "View details"} <span className="km-arrow" aria-hidden="true">→</span>
        <span className="km-visuallyHidden">: {webinar.title}</span>
      </Link>
    </article>
  );
}
